"use client";

import type { IconicVoiceItem } from "@/app/_api/elevenlabs/iconicVoices";

import { AspectRatio } from "@components/core/AspectRatio";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";
import { ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";

import { Block } from "./Block";

interface BlockIconicVoicesSliderClientProps {
  voices: IconicVoiceItem[];
}

const BlockIconicVoicesSliderClient = ({ voices }: BlockIconicVoicesSliderClientProps) => {
  const t = useTranslations("ClientKeys.IconicVoicesSlider");

  if (!voices || voices.length === 0) return null;

  const renderVoiceItem = (voice: IconicVoiceItem) => {
    const Wrapper = voice.requestingDisabled ? "div" : "a";
    const linkProps = voice.requestingDisabled ? {} : { href: voice.requestUrl };

    return (
      <Wrapper
        className='group relative block w-full overflow-hidden rounded-xl text-left no-underline transition-shadow duration-300 ease-out hover:shadow-[0_0_0_1px_rgba(0,0,0,0.07),_0_2px_4px_0_rgba(0,0,0,0.04)]'
        {...linkProps}
      >
        <AspectRatio className='bg-darker' ratio='4x3' rounded={false}>
          {voice.imageUrl && (
            <Image
              alt={voice.name}
              className='object-cover object-top'
              fill
              sizes='(min-width: 1024px) 33vw, 90vw'
              src={voice.imageUrl}
            />
          )}
        </AspectRatio>
        <div className='effect-color space-y-4 bg-dark px-8 pb-40 pt-28 text-center group-hover:bg-default'>
          <div className='tw-flex tw-flex-col tw-items-center tw-gap-1 sm:tw-flex-row sm:tw-justify-center sm:tw-gap-2'>
            <p className='f-paragraph-01'>{voice.name}</p>
            {voice.licenseType === "demo" && (
              <span className='tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-gray-200 tw-px-2 tw-py-0.5 tw-text-gray-500 tw-type-2xs'>
                {t("demo_only")}
              </span>
            )}
          </div>
          {voice.shortBio && <p className='f-paragraph-03 line-clamp-2 text-light'>{voice.shortBio}</p>}
        </div>
        {!voice.requestingDisabled && (
          <div className='effect-opacity absolute right-8 top-8 flex h-32 w-32 -rotate-45 items-center justify-center rounded-full bg-default opacity-0 shadow-outline group-hover:opacity-100'>
            <ChevronRight size={20} />
          </div>
        )}
      </Wrapper>
    );
  };

  return (
    <Block name='iconic-voices-slider'>
      <ScrollGrid items={voices} renderItem={renderVoiceItem} />
    </Block>
  );
};

BlockIconicVoicesSliderClient.displayName = "BlockIconicVoicesSliderClient";

export { BlockIconicVoicesSliderClient };
